import { Injectable } from '@angular/core';
import { mergeMap, Observable, of, take } from 'rxjs';
import { HttpParams } from '@angular/common/http';

import { DprCard, DprCards } from '@dpr/models';
import { DprApprover, DprDetails, DprOnlineApproval } from '@shared/models/dpr';
import { DprBasePersistenceService } from '.';
import { OfflineFacade } from '@shared/state/facades';
import { DprOfflineService } from '../sync';

@Injectable()
export class DprLocalPersistenceService extends DprBasePersistenceService {
  constructor(
    private offlineFacade: OfflineFacade,
    private dprOfflineService: DprOfflineService
  ) {
    super();
  }

  getDprCards(filters: HttpParams): Observable<DprCards> {
    return this.offlineFacade.query.combinedDprDetailsList$.pipe(
      take(1),
      mergeMap((dprDetails) => {
        if (+filters.get('pageNumber') > 1) {
          return of({
            dprCards: [],
            totalCount: 0,
          });
        }
        return of({
          dprCards: dprDetails.map(this.dprOfflineService.convertToDPRCard),
          totalCount: dprDetails.length,
        });
      })
    );
  }

  lockDpr(
    dprDetailId: number | string,
    _subprojectId: number,
    lockDate: string
  ): Observable<DprDetails> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprOfflineService.lockDpr(dprDetailId, lockDate);
  }

  unlockDpr(dprDetailId: number | string): Observable<number | string> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprOfflineService.unlockDpr(dprDetailId);
  }

  createDpr(subprojectId: number, date: Date): Observable<DprCard> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprOfflineService.createDpr(subprojectId, date);
  }

  createDprApprover(
    model: DprApprover,
    _subprojectId: number
  ): Observable<DprApprover> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprOfflineService.createDprApprover(model);
  }
  updateDprApprover(
    model: DprApprover,
    _subprojectId: number
  ): Observable<DprApprover> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprOfflineService.updateDprApprover(model);
  }

  deleteDprApprover(
    dprApproverId: number | string,
    _subprojectId: number,
    dprDetailId: number | string
  ): Observable<void> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprOfflineService.deleteDprApprover(dprApproverId, dprDetailId);
  }

  syncDprOnlineApproval(
    model: DprOnlineApproval,
    _subprojectId: number
  ): Observable<{
    dprOnlineApproval: DprOnlineApproval;
    dprDetail: DprDetails;
  }> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprOfflineService.saveDprOnlineApproval(model);
  }
}
